/**
 * `/crm/organizations/:id` — one organization: the people who work there, its
 * deals, and what is planned with it.
 */
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { PersonDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { Columns } from '../../components/Columns';
import { Details } from '../../components/Details';
import { EntityDetail } from '../../components/EntityDetail';
import { Panel } from '../../components/Panel';
import { EntityTable } from '../../components/EntityTable';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useOrganizationActions } from './actions';
import { ActivityPanel } from './ActivityPanel';
import { getOrganization, listPersons } from './api';
import { RelatedDeals } from './RelatedDeals';

function People({ organizationId }: { organizationId: number }) {
  const { t } = useTranslation('crm');
  const list = useListQuery({ defaultSort: 'name', defaultLimit: 10, resetKey: organizationId });
  const persons = useAsync(() => listPersons({ ...list.query, organizationId }), [organizationId, list.key]);

  return (
    <div>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {t('person.title')}
      </Typography>
      <AsyncContent state={persons}>
        {({ persons: rows, page }) => (
          <EntityTable<PersonDto>
            rows={rows}
            rowKey={(person) => person.id}
            list={list}
            page={page}
            empty={{ icon: <PersonOutlinedIcon fontSize="inherit" />, title: t('person.noneHere'), dense: true }}
            columns={[
              {
                field: 'name',
                label: t('field.name'),
                sortable: true,
                render: (person) => (
                  <Link component={RouterLink} to={`/crm/persons/${person.id}`} underline="hover" sx={{ fontWeight: 500 }}>
                    {person.name}
                  </Link>
                ),
              },
              { field: 'email', label: t('field.email'), render: (person) => person.email ?? '' },
              { field: 'phone', label: t('field.phone'), render: (person) => person.phone ?? '' },
            ]}
          />
        )}
      </AsyncContent>
    </div>
  );
}

export default function OrganizationPage() {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  const id = Number(useParams()['id']);
  const organization = useAsync(() => getOrganization(id), [id]);
  const actions = useOrganizationActions({
    onChanged: (key) => (key === 'delete' ? navigate('/crm/organizations') : organization.reload()),
  });

  return (
    <AsyncContent state={organization}>
      {({ organization: record }) => (
        <EntityDetail
          backTo="/crm/organizations"
          backLabel={t('organization.back')}
          title={record.name}
          description={record.address ?? undefined}
          entity={actions.forRow(record)}
          error={actions.error}
        >
          <Columns split={[1, 1.4]} align="start">
            <Stack spacing={3}>
              <Panel>
                <Details
                  rows={[
                    [t('field.address'), record.address],
                    [t('field.owner'), record.owner.name],
                  ]}
                />
              </Panel>
              <People organizationId={record.id} />
              <RelatedDeals filter={{ organizationId: record.id }} />
            </Stack>
            <ActivityPanel
              filter={{ organizationId: record.id }}
              link={{ organization: { id: record.id, name: record.name } }}
            />
          </Columns>
        </EntityDetail>
      )}
    </AsyncContent>
  );
}
